import type { Sheet } from "../shared/schema";
import type { WorkspaceModel } from "./useWorkspace";
import { SheetSizeControls } from "./SheetSizeControls";
import { overflowingSheetItems } from "./export-overflow";
import "./sheet-file-controls.css";

/** The same sheet facts and controls are used in the drawing inspector and properties. */
export function SheetProperties({
  model,
  sheet,
}: {
  model: WorkspaceModel;
  sheet: Sheet;
}) {
  const { editor, canEdit } = model;
  const disabled = !canEdit || sheet.isLocked;
  const overflow = overflowingSheetItems(editor, sheet);
  const background = /^#[0-9a-fA-F]{6}$/.test(
    String(sheet.meta.backgroundColor),
  )
    ? String(sheet.meta.backgroundColor)
    : "#ffffff";
  const original = /^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/.test(
    sheet.props.source,
  )
    ? sheet.props.source
    : null;
  return (
    <div className="sheet-properties">
      <section className="sheet-properties-section">
        <h4>Sheet</h4>
        <label>
          Name
          <input
            aria-label="Sheet name"
            maxLength={100}
            value={sheet.props.name}
            disabled={disabled}
            onChange={(event) =>
              editor.updateShape({
                id: sheet.id,
                type: "sheet",
                props: { name: event.target.value },
              })
            }
          />
        </label>
        <SheetSizeControls
          key={`${sheet.id}:${sheet.props.w}:${sheet.props.h}`}
          editor={editor}
          sheet={sheet}
          disabled={disabled}
        />
      </section>
      <section className="sheet-properties-section">
        <h4>Drawing</h4>
        {sheet.props.source && (
          <div className="property-row">
            <span>Source page</span>
            <strong>{sheet.props.page}</strong>
          </div>
        )}
        {original && (
          <a
            className="sheet-source-download"
            href={original}
            download={sheet.props.name}
            aria-label={`Download original for ${sheet.props.name}`}
          >
            Download original
          </a>
        )}
        <div className="property-row">
          <span>Scale</span>
          <strong>{sheet.props.metersPerUnit ? "Set" : "Not set"}</strong>
        </div>
        {sheet.props.calibration && (
          <small className="calibration-info">
            {sheet.props.calibration.startsWith("Example:")
              ? "Example scale: based on a 16.00 m distance."
              : sheet.props.calibration}
          </small>
        )}
        <button
          className="calibrate-button"
          disabled={disabled}
          onClick={() => model.choose("calibrate")}
        >
          Set scale
        </button>
      </section>
      <section className="sheet-properties-section">
        <h4>Publication</h4>
        <div className="form-grid">
          <label>
            Revision
            <input
              aria-label="Sheet revision"
              maxLength={24}
              value={sheet.props.revision}
              disabled={disabled}
              onChange={(event) =>
                editor.updateShape({
                  id: sheet.id,
                  type: "sheet",
                  props: { revision: event.target.value },
                })
              }
            />
          </label>
          <label>
            Date
            <input
              aria-label="Sheet revision date"
              type="date"
              value={String(sheet.meta.iterationDate ?? "")}
              disabled={disabled}
              onChange={(event) =>
                editor.updateShape({
                  id: sheet.id,
                  type: "sheet",
                  meta: { ...sheet.meta, iterationDate: event.target.value },
                })
              }
            />
          </label>
        </div>
        {!sheet.props.source && (
          <label className="sheet-background-field">
            Background
            <input
              aria-label="Sheet paper background"
              type="color"
              value={background}
              disabled={disabled}
              onChange={(event) => {
                editor.markHistoryStoppingPoint("Change paper background");
                editor.updateShape({
                  id: sheet.id,
                  type: "sheet",
                  meta: { ...sheet.meta, backgroundColor: event.target.value },
                });
              }}
            />
          </label>
        )}
      </section>
      <section className="sheet-properties-section">
        <h4>Export fit</h4>
        <div className="sheet-overflow-summary" role="status">
          {overflow.length ? (
            <>
              <strong>
                {overflow.length} item{overflow.length === 1 ? "" : "s"} outside
                paper
              </strong>
              <small>
                These items remain on the board and will be clipped in PDF
                export. Resize the paper or move them inside its edge.
              </small>
            </>
          ) : (
            <small>All sheet contents fit inside the paper.</small>
          )}
        </div>
      </section>
    </div>
  );
}
